import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, UserPlus, Building2, Globe2, Search, Zap, Shield, TrendingUp } from "lucide-react";
import { Layout } from "@/components/Layout";
import { CandidateCard } from "@/components/CandidateCard";
import { JobCard } from "@/components/JobCard";
import { api } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";

const Counter = ({ label, value }) => (
  <div className="text-center">
    <div className="font-display text-2xl sm:text-3xl font-extrabold text-navy">{Number(value || 0).toLocaleString()}</div>
    <div className="mt-1 text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
  </div>
);

export default function Home() {
  const { t } = useLang();
  const [counters, setCounters] = useState({});
  const [feed, setFeed] = useState({ available_now: [], jobs_today: [] });

  useEffect(() => {
    api.get("/stats/counters").then((r) => setCounters(r.data)).catch(() => {});
    api.get("/stats/home").then((r) => setFeed(r.data)).catch(() => {});
  }, []);

  return (
    <Layout>
      {/* HERO */}
      <section className="hero-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 sm:pt-24">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-navy shadow-sm">
              <span className="live-dot" /> {t("demo_data")}
            </span>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight text-navy">
              Find Work. Find People. <span className="gjt-gradient-text">Anywhere.</span>
            </h1>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600">{t("hero_sub")}</p>
          </div>

          {/* Dual CTAs */}
          <div className="mt-10 grid gap-5 sm:grid-cols-2 max-w-4xl">
            <Link to="/signup" data-testid="cta-find-work" className="gjt-card group p-6 flex items-center gap-4 border-l-4 border-l-brand-blue">
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-brand-bluelight text-brand-blue"><UserPlus className="h-6 w-6" /></div>
              <div className="flex-1">
                <div className="font-display text-base font-bold text-navy">{t("cta_work_title")}</div>
                <div className="text-sm text-emerald-600 font-semibold">{t("cta_work_sub")}</div>
              </div>
              <ArrowRight className="h-5 w-5 text-slate-300 transition-transform group-hover:translate-x-1 group-hover:text-brand-blue" />
            </Link>
            <Link to="/signup?role=employer" data-testid="cta-find-people" className="gjt-card group p-6 flex items-center gap-4 border-l-4 border-l-brand-orange">
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-orange-50 text-brand-orange"><Building2 className="h-6 w-6" /></div>
              <div className="flex-1">
                <div className="font-display text-base font-bold text-navy">{t("cta_people_title")}</div>
                <div className="text-sm font-semibold text-brand-orange">{t("cta_people_sub")}</div>
              </div>
              <ArrowRight className="h-5 w-5 text-slate-300 transition-transform group-hover:translate-x-1 group-hover:text-brand-orange" />
            </Link>
          </div>

          {/* Counters */}
          <div className="mt-12 grid grid-cols-3 gap-6 sm:grid-cols-6 rounded-2xl border border-slate-200 bg-white/70 p-6 backdrop-blur">
            <Counter label={t("stat_candidates")} value={counters.available_candidates} />
            <Counter label={t("stat_jobs")} value={counters.jobs_available} />
            <Counter label={t("stat_countries")} value={counters.countries} />
            <Counter label={t("stat_cities")} value={counters.cities} />
            <Counter label={t("stat_skills")} value={counters.skills} />
            <Counter label={t("stat_employers")} value={counters.employers} />
          </div>
        </div>
      </section>

      {/* AVAILABLE NOW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-navy flex items-center gap-2">
            <span className="live-dot" /> {t("available_now")}
          </h2>
          <Link to="/find-people" className="text-sm font-semibold text-brand-blue hover:underline flex items-center gap-1">
            {t("nav_find_people")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {feed.available_now.slice(0, 4).map((c, i) => <CandidateCard key={c.id} c={c} index={i} />)}
        </div>
      </section>

      {/* JOBS TODAY */}
      <section className="bg-slate-50 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-navy">🔥 {t("jobs_today")}</h2>
            <Link to="/jobs" className="text-sm font-semibold text-brand-blue hover:underline flex items-center gap-1">
              {t("nav_find_jobs")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {feed.jobs_today.slice(0, 4).map((j, i) => <JobCard key={j.id} job={j} index={i} />)}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid gap-6 md:grid-cols-4">
          {[
            { icon: Globe2, title: "Global Search Engine", desc: "A worldwide workforce search across countries, cities and languages." },
            { icon: Zap, title: "Availability-First", desc: "Find people who are ready to work right now, not months ago." },
            { icon: Shield, title: "Free for Life", desc: "Candidates never pay. Employers get their first 5 posts free." },
            { icon: TrendingUp, title: "HomeRank Relevance", desc: "A Google-style ranking that rewards quality, not just money." },
          ].map((f, i) => (
            <div key={i} className="gjt-card p-6 gjt-fade-up" style={{ animationDelay: `${i * 70}ms` }}>
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-brand-bluelight text-brand-blue"><f.icon className="h-5 w-5" /></div>
              <h3 className="mt-4 font-display text-base font-bold text-navy">{f.title}</h3>
              <p className="mt-1.5 text-sm text-slate-600">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </Layout>
  );
}
